import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProductTypesProvider } from "@/context/ProductTypesContext";
import { ProtectedRoute, ClientRoute } from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import Activate from "@/pages/Activate";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import Kiosk from "@/pages/Kiosk";
import Dashboard from "@/pages/Dashboard";
import MilkIntake from "@/pages/MilkIntake";
import RawMaterials from "@/pages/RawMaterials";
import Suppliers from "@/pages/Suppliers";
import FatCalculator from "@/pages/FatCalculator";
import YieldSimulator from "@/pages/YieldSimulator";
import Productions from "@/pages/Productions";
import Templates from "@/pages/Templates";
import Haccp from "@/pages/Haccp";
import Traceability from "@/pages/Traceability";
import Planning from "@/pages/Planning";
import Users from "@/pages/Users";
import Stock from "@/pages/Stock";
import Clients from "@/pages/Clients";
import PriceLists from "@/pages/PriceLists";
import ProductPackaging from "@/pages/ProductPackaging";
import Products from "@/pages/Products";
import Orders from "@/pages/Orders";
import DeliveryNotes from "@/pages/DeliveryNotes";
import CompanySettings from "@/pages/CompanySettings";
import Permissions from "@/pages/Permissions";
import ActivityLog from "@/pages/ActivityLog";
import ClientPortal from "@/pages/ClientPortal";
import ClientOrders from "@/pages/ClientOrders";
import Operators from "@/pages/Operators";
import TimeClock from "@/pages/TimeClock";
import Tasks from "@/pages/Tasks";
import Complaints from "@/pages/Complaints";
import Billing from "@/pages/Billing";
import Subproducts from "@/pages/Subproducts";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import StockMovements from "@/pages/StockMovements";
import Losses from "@/pages/Losses";
import Nonconformities from "@/pages/Nonconformities";
import Guide from "@/pages/Guide";

function App() {
  useEffect(() => {
    document.title = "LactoPilot";
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ProductTypesProvider>
          <Routes>
            {/* Public marketing landing page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/superadmin" element={<SuperAdmin />} />

            {/* Public shareable time-clock kiosk (no auth) */}
            <Route path="/kiosk" element={<Kiosk />} />

            {/* Authenticated ERP (staff) — everything under /app/* */}
            <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/app/milk" element={<ProtectedRoute><MilkIntake /></ProtectedRoute>} />
            <Route path="/app/raw-materials" element={<ProtectedRoute><RawMaterials /></ProtectedRoute>} />
            <Route path="/app/suppliers" element={<ProtectedRoute><Suppliers category="milk" /></ProtectedRoute>} />
            <Route path="/app/material-suppliers" element={<ProtectedRoute><Suppliers category="other" /></ProtectedRoute>} />
            <Route path="/app/calculator" element={<ProtectedRoute><FatCalculator /></ProtectedRoute>} />
            <Route path="/app/yield-simulator" element={<ProtectedRoute><YieldSimulator /></ProtectedRoute>} />
            <Route path="/app/productions" element={<ProtectedRoute><Productions /></ProtectedRoute>} />
            <Route path="/app/planning" element={<ProtectedRoute><Planning /></ProtectedRoute>} />
            <Route path="/app/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
            <Route path="/app/haccp" element={<ProtectedRoute><Haccp /></ProtectedRoute>} />
            <Route path="/app/traceability" element={<ProtectedRoute><Traceability /></ProtectedRoute>} />
            <Route path="/app/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />
            <Route path="/app/subproducts" element={<ProtectedRoute><Subproducts /></ProtectedRoute>} />
            <Route path="/app/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/app/delivery-notes" element={<ProtectedRoute><DeliveryNotes /></ProtectedRoute>} />
            <Route path="/app/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
            <Route path="/app/price-lists" element={<ProtectedRoute><PriceLists /></ProtectedRoute>} />
            <Route path="/app/product-packaging" element={<ProtectedRoute><ProductPackaging /></ProtectedRoute>} />
            <Route path="/app/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><CompanySettings /></ProtectedRoute>} />
            <Route path="/app/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
            <Route path="/app/activity" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
            <Route path="/app/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
            <Route path="/app/operators" element={<ProtectedRoute><Operators /></ProtectedRoute>} />
            <Route path="/app/time-clock" element={<ProtectedRoute><TimeClock /></ProtectedRoute>} />
            <Route path="/app/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/app/complaints" element={<ProtectedRoute><Complaints /></ProtectedRoute>} />
            <Route path="/app/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/app/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
            <Route path="/app/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/app/stock-movements" element={<ProtectedRoute><StockMovements /></ProtectedRoute>} />
            <Route path="/app/losses" element={<ProtectedRoute><Losses /></ProtectedRoute>} />
            <Route path="/app/nonconformities" element={<ProtectedRoute><Nonconformities /></ProtectedRoute>} />
            <Route path="/app/guide" element={<ProtectedRoute><Guide /></ProtectedRoute>} />

            {/* Client portal */}
            <Route path="/portal" element={<ClientRoute><ClientPortal /></ClientRoute>} />
            <Route path="/portal/orders" element={<ClientRoute><ClientOrders /></ClientRoute>} />
          </Routes>
          </ProductTypesProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
